<template>
  <transition name="category-panel-dropdown">
    <div v-if="show" class="category-panel" @click.stop>
      <div class="panel-header">
        <div class="header-top">
          <h4 class="panel-title">{{ title }}</h4>
          <div class="selected-info" v-if="selectedName">已选: {{ selectedName }}</div>
        </div>

        <!-- 搜索框 -->
        <div class="search-box">
          <el-input
            v-model="searchKeyword"
            :placeholder="searchPlaceholder"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            @input="handleSearch"
            @clear="clearSearch"
          ></el-input>
        </div>
      </div>

      <div class="panel-content">
        <el-checkbox-group v-model="checkList" @change="changeCheck">
          <div class="category-tree">
            <!-- 搜索模式 -->
            <template v-if="isSearchMode">
              <div class="search-results">
                <div class="level-title">搜索结果</div>
                <div class="level-list">
                  <div
                    v-for="item in searchResults"
                    :key="String(item.value) + '-' + item.name"
                    class="category-item search-result-item"
                    @click="selectSearchResult(item)"
                  >
                    <div class="item-content">
                      <div class="checkbox-wrapper" @click.stop>
                        <el-checkbox :label="item.value"></el-checkbox>
                      </div>
                      <div class="text-wrapper">
                        <span class="item-text" v-html="highlightText(item.name)"></span>
                        <div class="item-path" v-if="item.path">{{ item.path }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 搜索结果为空 -->
                <div v-if="searchResults.length === 0" class="empty-state">
                  <i class="el-icon-search"></i>
                  <p>未找到匹配的分类</p>
                </div>
              </div>
            </template>

            <!-- 正常模式 -->
            <template v-else>
              <div v-for="(level, levelIndex) in categoryLevels" :key="levelIndex" class="category-level">
                <div class="level-title">{{ getLevelTitle(levelIndex) }}</div>
                <div class="level-list">
                  <div
                    v-for="item in level"
                    :key="String(item.value) + '-' + item.name"
                    :class="{
                      'category-item': true,
                      active: isActiveItem(levelIndex, item),
                      'has-children': item.children && item.children.length > 0
                    }"
                    @click="expandCategory(levelIndex, item)"
                  >
                    <div class="item-content">
                      <div class="checkbox-wrapper" @click.stop>
                        <el-checkbox :label="item.value"></el-checkbox>
                      </div>
                      <div class="text-wrapper">
                        <span class="item-text">{{ item.name }}</span>
                      </div>
                      <div class="icon-wrapper" v-if="item.children && item.children.length > 0">
                        <i class="el-icon-arrow-right"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 空状态提示 -->
              <div v-if="categoryLevels.length === 1 && categoryLevels[0].length === 0" class="empty-state">
                <i class="el-icon-folder-opened"></i>
                <p>暂无分类数据</p>
              </div>
            </template>
          </div>
        </el-checkbox-group>
      </div>

      <div class="panel-footer">
        <div class="action-buttons">
          <el-button size="small" class="btn-cancel" @click.stop="cancel">
            取消
          </el-button>
          <el-button size="small" class="btn-reset" @click.stop="reset" :disabled="checkList.length === 0">
            重置
          </el-button>
          <el-button type="primary" size="small" class="btn-confirm" @click.stop="confirm">
            确定选择
          </el-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "CategorieSelectNew",
  props: {
    title: {
      type: String,
      default: "分类选择"
    },
    searchPlaceholder: {
      type: String,
      default: "搜索分类名称..."
    }
  },
  data() {
    return {
      show: false,
      list: [],
      categoryLevels: [], // 存储所有级别的分类数据
      selectedPath: [], // 存储当前选中的路径
      checkList: [],
      clickOutsideHandler: null,
      searchKeyword: "", // 搜索关键词
      searchResults: [], // 搜索结果
      isSearchMode: false // 是否处于搜索模式
    };
  },
  computed: {
    // 已选名称（checkList 存的是 value）
    selectedName() {
      if (!this.checkList.length) return "";
      const item = this.findByValue(this.list, this.checkList[0]);
      return item ? item.name : String(this.checkList[0]);
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // 延迟添加事件监听，避免立即触发
        setTimeout(() => {
          this.clickOutsideHandler = this.handleClickOutside.bind(this);
          document.addEventListener("click", this.clickOutsideHandler);
        }, 100);
      } else {
        this.removeClickListener();
        // 关闭时清空搜索状态
        this.clearSearch();
      }
    }
  },
  beforeDestroy() {
    this.removeClickListener();
  },
  methods: {
    open(show, list, selected) {
      this.show = show;
      this.list = list || [];
      const hit = selected ? this.findByValue(this.list, selected) : null;
      this.checkList = hit ? [hit.value] : [];
      this.categoryLevels = [this.list]; // 初始化第一级
      this.selectedPath = [];
      this.clearSearch();
    },

    // 在树中按 value 或 name 查找节点
    findByValue(nodes, search) {
      if (!Array.isArray(nodes)) return null;
      for (const node of nodes) {
        if (String(node.value) === String(search) || node.name === search) return node;
        if (node.children && node.children.length > 0) {
          const found = this.findByValue(node.children, search);
          if (found) return found;
        }
      }
      return null;
    },

    // 在树中按 value/name 查找目标节点的完整路径（第一级 → 目标节点本身）；品类等按层级提交 class/classBrand 需要
    findPath(nodes, search, path) {
      path = path || [];
      if (!Array.isArray(nodes)) return null;
      for (const node of nodes) {
        const nextPath = path.concat([{ id: node.id || 0, name: node.name, value: node.value, icon: node.icon || "" }]);
        if (String(node.value) === String(search) || node.name === search) return nextPath;
        if (node.children && node.children.length > 0) {
          const found = this.findPath(node.children, search, nextPath);
          if (found) return found;
        }
      }
      return null;
    },

    removeClickListener() {
      if (this.clickOutsideHandler) {
        document.removeEventListener("click", this.clickOutsideHandler);
        this.clickOutsideHandler = null;
      }
    },

    cancel() {
      this.show = false;
      this.removeClickListener();
      this.$emit("close");
    },

    reset() {
      this.checkList = [];
      this.categoryLevels = [this.list];
      this.selectedPath = [];
      this.clearSearch();
    },

    confirm() {
      if (this.checkList.length > 0) {
        const item = this.findByValue(this.list, this.checkList[0]);
        // path：从第一级到选中节点的完整路径，供品类等多级筛选按层级提交 class/classBrand
        const path = item ? this.findPath(this.list, this.checkList[0]) : [];
        this.$emit(
          "change",
          item ? { id: item.id || 0, name: item.name, value: item.value, icon: item.icon || "" } : null,
          path
        );
      } else {
        this.$emit("change", null, []);
      }
      this.cancel();
    },

    changeCheck(v) {
      // 保持单选逻辑
      if (v.length > 1) {
        this.checkList = [v[v.length - 1]];
      }
    },

    // 展开分类
    expandCategory(levelIndex, item) {
      // 更新选中路径
      this.selectedPath = this.selectedPath.slice(0, levelIndex);
      this.selectedPath[levelIndex] = item;

      // 如果该分类有子分类，则添加下一级
      if (item.children && item.children.length > 0) {
        // 更新分类级别显示
        this.categoryLevels = this.categoryLevels.slice(0, levelIndex + 1);
        this.categoryLevels.push(item.children);
      } else {
        // 如果没有子分类，则移除后面的所有级别
        this.categoryLevels = this.categoryLevels.slice(0, levelIndex + 1);
      }
    },

    // 判断是否为当前激活项
    isActiveItem(levelIndex, item) {
      return this.selectedPath[levelIndex] && this.selectedPath[levelIndex].value === item.value;
    },

    // 获取级别标题
    getLevelTitle(levelIndex) {
      const titles = ["全部分类", "一级分类", "二级分类", "三级分类", "四级分类", "五级分类", "六级分类"];
      return titles[levelIndex] || `分类${levelIndex + 1}`;
    },

    handleClickOutside(event) {
      if (this.show && !this.$el?.contains(event.target)) {
        this.cancel();
      }
    },

    // 搜索相关方法
    handleSearch() {
      if (this.searchKeyword.trim() === "") {
        this.clearSearch();
        return;
      }

      this.isSearchMode = true;
      this.searchResults = this.searchCategories(this.list, this.searchKeyword.trim());
    },

    clearSearch() {
      this.searchKeyword = "";
      this.searchResults = [];
      this.isSearchMode = false;
    },

    // 递归搜索分类
    searchCategories(categories, keyword, path = "") {
      let results = [];

      categories.forEach(category => {
        const currentPath = path ? `${path} / ${category.name}` : category.name;

        // 检查当前分类是否匹配
        if (category.name.toLowerCase().includes(keyword.toLowerCase())) {
          results.push({
            ...category,
            path: currentPath
          });
        }

        // 递归搜索子分类
        if (category.children && category.children.length > 0) {
          const childResults = this.searchCategories(category.children, keyword, currentPath);
          results = results.concat(childResults);
        }
      });

      return results;
    },

    // 高亮显示搜索关键词
    highlightText(text) {
      if (!this.searchKeyword) return text;

      const regex = new RegExp(`(${this.searchKeyword})`, "gi");
      return text.replace(regex, '<span class="highlight">$1</span>');
    },

    // 选择搜索结果
    selectSearchResult(item) {
      this.checkList = [item.value];
      const path = this.findPath(this.list, item.value) || [
        { id: item.id || 0, name: item.name, value: item.value, icon: item.icon || "" }
      ];
      this.$emit("change", { id: item.id || 0, name: item.name, value: item.value, icon: item.icon || "" }, path);
      this.cancel();
    }
  }
};
</script>

<style lang="scss" scoped>
.category-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e4e7ed;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 540px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%; /* 确保宽度与父元素相同 */
  min-width: 600px; /* 保持足够的最小宽度 */
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-light80) 100%);
  color: white;

  .header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .panel-title {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
    }

    .selected-info {
      font-size: 12px;
      background: rgba(255, 255, 255, 0.2);
      padding: 4px 8px;
      border-radius: 12px;
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .search-box {
    ::v-deep .el-input__inner {
      background: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: #333;

      &:focus {
        background: white;
        border-color: var(--color-primary);
      }

      &::placeholder {
        color: #999;
      }
    }

    ::v-deep .el-input__prefix {
      color: #666;
    }
  }
}

.panel-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  min-height: 300px;

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #909399;

    i {
      font-size: 32px;
      margin-bottom: 8px;
      opacity: 0.5;
    }

    p {
      margin: 0;
      font-size: 13px;
    }
  }
}

.category-tree {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  height: 100%;
  overflow-x: auto;
}

.category-level {
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
  min-width: 200px;

  .level-title {
    background: #f8f9fa;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    border-bottom: 1px solid #e8e8e8;
  }

  .level-list {
    flex: 1;
    overflow-y: auto;
    max-height: 320px;
  }
}

.category-item {
  border-bottom: 1px solid #f5f5f5;
  transition: all 0.2s ease;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f0f7ff;

    .item-text {
      color: var(--color-primary);
    }

    .icon-wrapper {
      color: var(--color-primary);
    }
  }

  &.active {
    background: #ecf5ff;
    border-left: 3px solid var(--color-primary);

    .item-text {
      color: var(--color-primary);
      font-weight: 600;
    }

    .icon-wrapper {
      color: var(--color-primary);
    }
  }

  .item-content {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    gap: 8px;
  }

  .checkbox-wrapper {
    flex-shrink: 0;

    // 隐藏复选框标签文字
    ::v-deep .el-checkbox__label {
      display: none;
    }

    ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: var(--color-primary);
      border-color: var(--color-primary);
    }

    ::v-deep .el-checkbox__inner {
      width: 14px;
      height: 14px;

      &::after {
        left: 4px;
        top: 1px;
        width: 3px;
        height: 6px;
      }
    }
  }

  .text-wrapper {
    flex: 1;
    min-width: 0;

    .item-text {
      font-size: 13px;
      color: #333;
      font-weight: 500;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.2s;
    }

    // 搜索结果的高亮样式
    .highlight {
      background-color: #ffeb3b;
      color: #e6a23c;
      padding: 0 2px;
      border-radius: 2px;
      font-weight: 600;
    }

    .item-path {
      font-size: 11px;
      color: #909399;
      margin-top: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .icon-wrapper {
    flex-shrink: 0;
    color: #c0c4cc;
    font-size: 12px;
    transition: color 0.2s, transform 0.2s;
  }

  &.has-children:hover .icon-wrapper {
    transform: translateX(2px);
  }
}

// 搜索结果特定样式
.search-results {
  grid-column: 1 / -1;

  .level-list {
    max-height: 360px !important;
  }

  .search-result-item {
    .item-content {
      padding: 12px 16px;
    }

    &:hover {
      background: #f0f7ff;
    }
  }
}

.panel-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .btn-cancel {
      border-color: #dcdfe6;
      font-size: 12px;
      padding: 6px 12px;

      &:hover {
        border-color: #c0c4cc;
        background-color: #f5f7fa;
      }
    }

    .btn-reset {
      color: #f56c6c;
      border-color: #f56c6c;
      font-size: 12px;
      padding: 6px 12px;

      &:hover {
        background-color: #fef0f0;
        border-color: #f78989;
      }

      &:disabled {
        color: #c0c4cc;
        border-color: #e4e7ed;
        background-color: #f5f7fa;
      }
    }

    .btn-confirm {
      background: linear-gradient(135deg, var(--color-primary) 0%, #66b1ff 100%);
      border: none;
      font-size: 12px;
      padding: 6px 16px;

      &:hover {
        background: linear-gradient(135deg, #66b1ff 0%, var(--color-primary) 100%);
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
      }

      &:disabled {
        background: #c0c4cc;
        transform: none;
        box-shadow: none;
      }
    }
  }
}

// 下拉动画
.category-panel-dropdown-enter-active {
  animation: category-panel-dropdown-in 0.25s ease-out;
}

.category-panel-dropdown-leave-active {
  animation: category-panel-dropdown-out 0.2s ease-in;
}

@keyframes category-panel-dropdown-in {
  0% {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes category-panel-dropdown-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
}

// 滚动条样式
.category-panel ::-webkit-scrollbar {
  width: 4px;
}

.category-panel ::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.category-panel ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.category-panel ::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// 响应式设计
@media (max-width: 768px) {
  .category-panel {
    max-height: 400px;
    min-width: unset;
    width: 100vw;
    left: 0;
    right: 0;
  }

  .category-tree {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .category-level {
    .level-list {
      max-height: 120px;
    }
  }

  .panel-header,
  .panel-content,
  .panel-footer {
    padding: 10px 12px;
  }

  .action-buttons {
    flex-wrap: wrap;

    .el-button {
      flex: 1;
      min-width: 70px;
    }
  }

  .search-results .level-list {
    max-height: 280px !important;
  }
}
</style>
